import { endOfDay } from 'date-fns'
import { sortBy } from 'lodash-es'

import type { DataPoint } from '@/types/services/common'
import type { ClientTransfer } from '@/types/services/transfers'

/**
 * Deposits and withdrawals aggregated by date
 */
export interface DepositsWithdrawals {
  prev: DepositsWithdrawals | null
  date: string
  cumulativeValue: number
  deposits: number
  withdrawals: number
}

/**
 * Map of deposits/withdrawals by date timestamp
 */
export interface DWSByDate {
  [key: number]: DepositsWithdrawals
}

/**
 * Result of parsing transfers
 */
export interface ParsedTransfers {
  byDate: DWSByDate
  totals: {
    deposits: number
    withdrawals: number
  }
}

/**
 * Normalizes a transfer-related date value to the same end-of-day timestamp key used in DWSByDate.
 *
 * @param value - DataPoint or date-like value used to address transfer entries
 * @returns End-of-day timestamp key in milliseconds
 */
export function getTransferPointTime(
  value: DataPoint | DataPoint['x']
): number {
  const pointValue =
    typeof value === 'object' && value !== null && 'x' in value
      ? value.x
      : value

  if (typeof pointValue === 'number') {
    return pointValue
  }

  return endOfDay(new Date(pointValue)).getTime() - 999
}

/**
 * Parses client transfers into deposits/withdrawals aggregated by date.
 *
 * @param transfers - Array of client transfers from API
 * @param getEntityDate - Function to convert date to entity timezone
 * @returns Parsed transfers with byDate map and totals
 */
export function parseTransfersToDWS(
  transfers: ClientTransfer[],
  getEntityDate: (date: Date) => Date
): ParsedTransfers {
  const byDate: DWSByDate = {}
  let totalDeposits = 0
  let totalWithdrawals = 0
  let prevDateTime: number | null = null

  sortBy(transfers, 'date').forEach((transfer) => {
    if (transfer.group === 'fee_withdrawal') {
      return
    }
    if (transfer.status === 'pending') {
      return
    }

    const date = new Date(transfer.date)
    const entityDate = getEntityDate(date)
    const dateTime = getTransferPointTime(entityDate)

    if (!(dateTime in byDate)) {
      const prev = prevDateTime ? byDate[prevDateTime] : null
      byDate[dateTime] = {
        prev,
        date: transfer.date,
        cumulativeValue: prev ? prev.cumulativeValue : 0,
        deposits: 0,
        withdrawals: 0,
      }
    }

    const value = transfer.target_amounts[0].value

    if (!value) {
      return
    }

    if (transfer.group === 'deposit') {
      byDate[dateTime].deposits += value
      totalDeposits += value
    } else if (transfer.group === 'withdrawal') {
      byDate[dateTime].withdrawals -= value
      totalWithdrawals -= value
    }

    byDate[dateTime].cumulativeValue =
      (byDate[dateTime].prev?.cumulativeValue || 0) +
      byDate[dateTime].deposits +
      byDate[dateTime].withdrawals

    prevDateTime = dateTime
  })

  return {
    byDate,
    totals: {
      deposits: totalDeposits,
      withdrawals: totalWithdrawals,
    },
  }
}
